import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { useEffect } from 'react';
import { CallbackInterface, useRecoilCallback, useRecoilValue } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import {
    DicomContour,
    RoiGenerationAlgorithmType,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import {
    BODY_CONTOUR_OPERATION,
    domainEventBus,
    EventSource,
    RoiApprovalStatusChangeRequestedEvent,
    RoiColorChangeRequestedEvent,
    RoiContourModificationStartedEvent,
    RoiContoursModifiedEvent,
    RoiContourThicknessChangeRequestedEvent,
    RoiCreationRequestedEvent,
    RoiDeletionRequestedEvent,
    RoiElectronDensityOverrideChangeRequestedEvent,
    RoiMassDensityOverrideChangeRequestedEvent,
    RoiDescriptionChangeRequestedEvent,
    RoiGenerationAlgorithmChangeRequestedEvent,
    RoiSelectionRequestedEvent,
    RoiInterpretedTypeChangeRequestedEvent,
    RoiNameChangeRequestedEvent,
    RoiPointCoordinatesChangeRequestedEvent,
    RoiPropertiesUpdatedEvent,
    RoiVisibilityChangeRequestedEvent,
    RoiLockChangeRequestedEvent,
    RoiCopyRequestedEvent,
    RoiSdfCopyOverRequestedEvent,
} from '~/domain/events';
import { editBlockedMessageId } from '~/components/Structures/editBlockedMessage';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    getContourUndoService,
    getRoiBaselineStore,
} from '~/domain/ServiceContainer';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { INITIAL_STATE_OPERATION } from '~/domain/undo';
import { StructureSet } from '~/domain/patient/structureset';
import { generateUniqueStructureName } from '~/domain/services/dicomProcessor';
import { RoiRef, RoiService } from '~/domain/services/structureSet/RoiService';
import { copyableInterpretedType } from '~/domain/services/structureSetService/structureSetCopyService';
import { ensureStructureSetsCurrent } from '~/hooks/ensureStructureSetsCurrent';
import { localization } from '~/i18n';
import { enqueueSnackbar } from '~/utils/notifications';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import {
    currentStructureSetState,
    structureSetReadModelFamily,
} from '~/state/atoms/structureSet/structureSetAtoms';
import { generateDarkerColor } from '~/utils/color';
import { redactDicomUids } from '~/utils/phiRedaction';

function updateRoiAndNotify(
    recoilInterface: CallbackInterface,
    structureSet: StructureSet | undefined,
    roiService: RoiService | undefined,
    roiNumber: number,
    applyMutation: (service: RoiService, ref: RoiRef) => void,
    propertyUpdate: Partial<DicomRoi>,
    source: EventSource
) {
    if (!structureSet || !roiService) {
        console.warn('No structure set / ROI service available');
        return;
    }

    try {
        // Resolve roiNumber -> domain Roi so we can address the
        // mutation by branded RoiId. RoiService wraps the mutation in a
        // command() block, so the change emits a coalesced changeset.
        const roi = structureSet.rois.find((r) => r.roiNumber === roiNumber);
        if (!roi) {
            throw new Error(`ROI ${roiNumber} not found in the structure set`);
        }
        applyMutation(roiService, { id: roi.id });
        const { set } = recoilInterface;
        set(
            currentStructureSetState,
            createStructureSetReadModel(structureSet)
        );

        return domainEventBus.publish(
            new RoiPropertiesUpdatedEvent(
                structureSet.dicomUid,
                roiNumber,
                propertyUpdate,
                source
            )
        );
    } catch (error) {
        console.error(`Failed to update ROI ${roiNumber}:`, error);
        throw error;
    }
}

function selectRoi(
    recoilInterface: CallbackInterface,
    structureSet: StructureSet | undefined,
    roiNumber: number
    // source: EventSource
) {
    if (!structureSet) {
        console.warn('No structure set available');
        return;
    }

    try {
        if (!structureSet.rois.some((r) => r.roiNumber === roiNumber)) {
            throw new Error(`ROI ${roiNumber} not found in the structure set`);
        }

        const { set } = recoilInterface;
        set(selectedROIState, roiNumber);

        // // could send a RoiSelectedEvent here if needed in future
        // return domainEventBus.publish(
        //     new RoiSelectedEvent(structureSetUid, roiNumber, source);
        // );
    } catch (error) {
        console.error(`Failed to select ROI ${roiNumber}:`, error);
        throw error;
    }
}

/**
 * Application service layer for {@link DicomRoi} operations within {@link StructureSet}.
 *
 * Processes ROI-specific domain events by coordinating updates to individual ROIs
 * within the structure set.
 */
export function useRoiHandlers() {
    const structureSetReadModel = useRecoilValue(currentStructureSetState);
    // Resolved through the edit target: the grid Environment that shows the
    // active set, so a flush, copy or texture change lands in the texture
    // the tiles draw, together with the dataset write.
    const { environment: env } = useActiveEditTarget();
    const session = useSessionOrNull();
    const roiService = session?.roiService;
    const structureSet = structureSetReadModel?.dicomUid
        ? session?.patient.getStructureSetWithSopInstanceUid(
              structureSetReadModel.dicomUid
          )
        : undefined;

    const handleVisibilityChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiVisibilityChangeRequestedEvent) => {
                // A request that changes nothing does nothing.
                const roi = structureSet?.rois.find(
                    (r) => r.roiNumber === event.roiNumber
                );
                if (roi?.dicomRoi.visible === event.visible) {
                    return;
                }
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSet,
                    roiService,
                    event.roiNumber,
                    (service, ref) => service.setVisibility(ref, event.visible),
                    { visible: event.visible },
                    event.source
                );
            },
        [structureSet, roiService]
    );

    const handleLockChangeRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiLockChangeRequestedEvent) => {
            await updateRoiAndNotify(
                recoilInterface,
                structureSet,
                roiService,
                event.roiNumber,
                (service, ref) => service.setLocked(ref, event.locked),
                { locked: event.locked },
                event.source
            );
        },
        [structureSet, roiService]
    );

    const handleNameChangeRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiNameChangeRequestedEvent) => {
            await updateRoiAndNotify(
                recoilInterface,
                structureSet,
                roiService,
                event.roiNumber,
                (service, ref) => service.rename(ref, event.name),
                { name: event.name },
                event.source
            );
        },
        [structureSet, roiService]
    );

    const handleColorChangeRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiColorChangeRequestedEvent) => {
            await updateRoiAndNotify(
                recoilInterface,
                structureSet,
                roiService,
                event.roiNumber,
                (service, ref) => service.setColor(ref, event.color),
                { color: event.color },
                event.source
            );
        },
        [structureSet, roiService]
    );

    const handleContourThicknessChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiContourThicknessChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSet,
                    roiService,
                    event.roiNumber,
                    (service, ref) =>
                        service.setContourThickness(
                            ref,
                            event.contourThickness
                        ),
                    { contourThickness: event.contourThickness },
                    event.source
                );
            },
        [structureSet, roiService]
    );

    const handleInterpretedTypeChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiInterpretedTypeChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSet,
                    roiService,
                    event.roiNumber,
                    (service, ref) =>
                        service.setInterpretedType(ref, event.interpretedType),
                    { interpretedType: event.interpretedType },
                    event.source
                );
            },
        [structureSet, roiService]
    );

    const handleElectronDensityOverrideChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiElectronDensityOverrideChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSet,
                    roiService,
                    event.roiNumber,
                    (service, ref) =>
                        service.setElectronDensityOverride(
                            ref,
                            event.electronDensityOverride
                        ),
                    { electronDensityOverride: event.electronDensityOverride },
                    event.source
                );
            },
        [structureSet, roiService]
    );

    const handleMassDensityOverrideChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiMassDensityOverrideChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSet,
                    roiService,
                    event.roiNumber,
                    (service, ref) =>
                        service.setMassDensityOverride(
                            ref,
                            event.massDensityOverride
                        ),
                    { massDensityOverride: event.massDensityOverride },
                    event.source
                );
            },
        [structureSet, roiService]
    );

    const handleDescriptionChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiDescriptionChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSet,
                    roiService,
                    event.roiNumber,
                    (service, ref) =>
                        service.setDescription(ref, event.roiDescription),
                    { roiDescription: event.roiDescription },
                    event.source
                );
            },
        [structureSet, roiService]
    );

    const handleGenerationAlgorithmChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiGenerationAlgorithmChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSet,
                    roiService,
                    event.roiNumber,
                    (service, ref) =>
                        service.setGenerationAlgorithm(
                            ref,
                            event.roiGenerationAlgorithm
                        ),
                    { roiGenerationAlgorithm: event.roiGenerationAlgorithm },
                    event.source
                );
            },
        [structureSet, roiService]
    );

    const handleApprovalStatusChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiApprovalStatusChangeRequestedEvent) => {
                // Per-ROI review is allowed only while the set is
                // UNAPPROVED; RoiService throws ReviewLockedError on a
                // signed-off set. The Review controls are disabled there,
                // so this legacy event path is a fail-safe — if it does
                // fire, updateRoiAndNotify logs and rethrows.
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSet,
                    roiService,
                    event.roiNumber,
                    (service, ref) => {
                        switch (event.approvalStatus) {
                            case DICOMApprovalStatus.APPROVED:
                                service.approve(ref, event.reviewerName);
                                break;
                            case DICOMApprovalStatus.REJECTED:
                                service.reject(ref, event.reviewerName);
                                break;
                            default:
                                service.unapprove(ref);
                        }
                    },
                    { approvalStatus: event.approvalStatus },
                    event.source
                );
            },
        [structureSet, roiService]
    );

    const handleRoiCreationRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiCreationRequestedEvent) => {
            try {
                if (!structureSet) {
                    console.warn('No structure set available for ROI creation');
                    return;
                }

                if (event.structureSetUid !== structureSet.dicomUid) {
                    console.warn(
                        'ROI creation event for another structure set ignored'
                    );
                    return;
                }

                const structureSetService = session?.structureSetService;
                if (!structureSetService) {
                    console.warn(
                        'No structure set service available for ROI creation'
                    );
                    return;
                }

                const { set } = recoilInterface;
                // Route through the tracked service so the creation
                // emits a 'created' changeset (projection layer and
                // persistence stay in sync). The outer command coalesces
                // the optional POI coordinate write into that same
                // changeset.
                const newRoi = session.context.tracker.command(() => {
                    const roi = structureSetService.createRoi(
                        { id: structureSet.id },
                        event.interpretedType,
                        ''
                    );
                    if (event.initialCoordinatesMm) {
                        const { x, y, z } = event.initialCoordinatesMm;
                        roi.setPointCoordinates(vec3.fromValues(x, y, z));
                        structureSet.dicomStructureSet.updateContourSequence(
                            roi.roiNumber
                        );
                    }
                    return roi;
                });

                set(
                    currentStructureSetState,
                    createStructureSetReadModel(structureSet)
                );
                set(hasUnsavedChangesState, true);

                // The engine's RegionOfInterest is created by EngineAdapter,
                // which publishes RoiCreatedEvent from the tracked 'created'
                // changeset emitted by structureSetService.createRoi above.
                // Publishing here too would double-create it, leaking the
                // first RegionOfInterest's GPU texture + event subscriptions.
                selectRoi(recoilInterface, structureSet, newRoi.roiNumber);
            } catch (error) {
                console.error(`Failed to handle ROI creation:`, error);
            }
        },
        [structureSet, session]
    );

    const handleRoiSelectionRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiSelectionRequestedEvent) => {
            const currentSelection =
                await recoilInterface.snapshot.getPromise(selectedROIState);

            if (currentSelection === event.roiNumber) {
                recoilInterface.set(selectedROIState, -1);
                return;
            }

            if (!structureSet) {
                console.warn('No structure set available for ROI selection');
                return;
            }

            selectRoi(recoilInterface, structureSet, event.roiNumber);
        },
        [structureSet]
    );

    const handleRoiDeletionRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiDeletionRequestedEvent) => {
            try {
                if (!structureSet) {
                    console.warn('No structure set available for ROI deletion');
                    return;
                }

                if (event.structureSetUid !== structureSet.dicomUid) {
                    console.warn(
                        'ROI deletion event for another structure set ignored'
                    );
                    return;
                }

                const roi = structureSet.rois.find(
                    (r) => r.roiNumber === event.roiNumber
                );
                if (!roi) {
                    console.warn(
                        `ROI ${event.roiNumber} not found in the structure set`
                    );
                    return;
                }

                const structureSetService = session?.structureSetService;
                if (!structureSetService) {
                    console.warn(
                        'No structure set service available for ROI deletion'
                    );
                    return;
                }

                const { set } = recoilInterface;
                // Route through the tracked service so the deletion
                // emits a 'deleted' changeset; it also keeps the rois[]
                // wrapper collection in sync with the DicomStructureSet.
                structureSetService.removeRoi({ id: roi.id });
                set(
                    currentStructureSetState,
                    createStructureSetReadModel(structureSet)
                );
                set(hasUnsavedChangesState, true);

                // Emit ROI deleted event for other systems (e.g., rendering engine)
                await domainEventBus.publish(
                    new RoiPropertiesUpdatedEvent(
                        structureSet.dicomUid,
                        event.roiNumber,
                        { deleted: true },
                        event.source
                    )
                );
            } catch (error) {
                console.error(
                    `Failed to handle ROI deletion for ROI ${event.roiNumber}:`,
                    error
                );
            }
        },
        [structureSet, session]
    );

    const handleContoursModified = useRecoilCallback(
        (recoilInterface) => async (event: RoiContoursModifiedEvent) => {
            try {
                // Attribute the edit to the event's owning structure set,
                // never the selected one: with several resident datasets a
                // stroke can finish on a structure set that is not currently
                // selected, and its undo history must land on its own set.
                const owningStructureSet =
                    session?.patient.getStructureSetWithSopInstanceUid(
                        event.structureSetUid
                    );
                if (!owningStructureSet) {
                    console.warn(
                        'Owning structure set not available for contour modification'
                    );
                    return;
                }

                const owningReadModel =
                    createStructureSetReadModel(owningStructureSet);
                const roiReadModel = owningReadModel.roiArray.find(
                    (r) => r.roiNumber === event.roiNumber
                );

                // A contour edit demotes an AUTOMATIC ROI to
                // SEMI_AUTOMATIC. Route through the service so the
                // change is tracked. Approved ROIs cannot have contours
                // edited, so this never hits the approval guard.
                //
                // Generation writes are exempt: an operation that AUTHORS
                // the contours (the body-contour extraction, which stamps
                // AUTOMATIC as part of the same apply) is not a manual edit
                // of an automatic result — demoting here would erase the
                // algorithm the very same operation just declared.
                const roi = owningStructureSet.rois.find(
                    (r) => r.roiNumber === event.roiNumber
                );
                if (
                    roi &&
                    roiService &&
                    event.modificationType !== BODY_CONTOUR_OPERATION &&
                    roiReadModel?.roiGenerationAlgorithm ===
                        RoiGenerationAlgorithmType.AUTOMATIC
                ) {
                    // Capture the untouched AI prediction before this edit demotes
                    // it, so the "vs original AI" difference overlay can compare
                    // later edits against it. This block only runs on an AUTOMATIC
                    // ROI's first edit (it is SEMI_AUTOMATIC after), and the store
                    // captures once per (structure set, ROI). Keyed by the owning
                    // set's UID so a same-numbered ROI in another loaded set gets
                    // its own baseline and cannot be served the wrong set's.
                    //
                    // Gate on the owning set's `generationModel`: only a structure
                    // set THIS app's AI produced this session has a true "original
                    // prediction" to compare against. An uploaded RTStruct can carry
                    // AUTOMATIC ROIs (some other tool segmented them), but the app
                    // never made those predictions — capturing the as-uploaded mask
                    // and labelling it "Original AI" would be misleading. The
                    // SEMI_AUTOMATIC demotion below still applies to every AUTOMATIC
                    // ROI, uploaded or not — editing an auto contour demotes it
                    // regardless of provenance.
                    if (
                        owningStructureSet.generationModel &&
                        event.initialPixels &&
                        event.initialGrid
                    ) {
                        await getRoiBaselineStore().capture(
                            owningStructureSet.dicomUid,
                            event.roiNumber,
                            event.initialPixels,
                            event.initialGrid
                        );
                    }
                    roiService.setGenerationAlgorithm(
                        { id: roi.id },
                        RoiGenerationAlgorithmType.SEMI_AUTOMATIC
                    );
                }

                if (event.pixels && event.grid && roiReadModel) {
                    const contourUndoService = getContourUndoService();

                    // Add initial-state first if this ROI has no undo history yet
                    if (
                        !contourUndoService.isRoiInitialized(
                            roiReadModel.structureSetUid,
                            event.roiNumber
                        ) &&
                        event.initialPixels &&
                        event.initialGrid
                    ) {
                        await contourUndoService.addContourOperation(
                            roiReadModel.structureSetUid,
                            roiReadModel,
                            event.initialPixels,
                            event.initialGrid,
                            INITIAL_STATE_OPERATION
                        );
                    }

                    await contourUndoService.addContourOperation(
                        roiReadModel.structureSetUid,
                        roiReadModel,
                        event.pixels,
                        event.grid,
                        event.modificationType
                    );
                }

                // The read model of the set selected when the event arrived is
                // refreshed regardless of which set was edited
                // (volume/statistics repaint). It is written by UID, so a
                // selection made during the awaits above stays.
                if (structureSet) {
                    recoilInterface.set(
                        structureSetReadModelFamily(structureSet.dicomUid),
                        createStructureSetReadModel(structureSet)
                    );
                }
                recoilInterface.set(hasUnsavedChangesState, true);
            } catch (error) {
                console.error(
                    `Failed to handle contour modification for ROI ${event.roiNumber}:`,
                    error
                );
            }
        },
        [session, structureSet, roiService]
    );

    const handlePointCoordinatesChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiPointCoordinatesChangeRequestedEvent) => {
                try {
                    const { x, y, z } = event.coordinates;
                    await updateRoiAndNotify(
                        recoilInterface,
                        structureSet,
                        roiService,
                        event.roiNumber,
                        (service, ref) => {
                            service.setPointCoordinates(
                                ref,
                                vec3.fromValues(x, y, z)
                            );
                        },
                        {},
                        event.source
                    );

                    // Persist updated point coordinates to the DICOM dataset
                    if (structureSet) {
                        structureSet.dicomStructureSet.updateContourSequence(
                            event.roiNumber
                        );
                    }
                } catch (error) {
                    console.error(
                        `Failed to update point coordinates for ROI ${event.roiNumber}:`,
                        error
                    );
                }
            },
        [structureSet, roiService]
    );

    const handleRoiCopyRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiCopyRequestedEvent) => {
            try {
                if (!structureSet) {
                    console.warn('No structure set available for ROI copy');
                    return;
                }

                if (event.structureSetUid !== structureSet.dicomUid) {
                    console.warn(
                        'ROI copy event for another structure set ignored'
                    );
                    return;
                }

                // Sync engine contour data to domain model so that
                // freshly drawn (unsaved) contours are available for
                // copying — including strokes made on grid tiles, which
                // live only in registry-held Environments. A set whose
                // edits were lost has a stale domain copy, and the copy
                // would carry its stale contours as new data.
                if (
                    !ensureStructureSetsCurrent(
                        getDatasetEnvironmentRegistry(),
                        [structureSet],
                        'Structure copy'
                    )
                ) {
                    return;
                }

                const sourceRoi = structureSet.dicomStructureSet.rois.get(
                    event.roiNumber
                );

                if (!sourceRoi) {
                    console.warn(`Source ROI ${event.roiNumber} not found`);
                    return;
                }

                const newName = generateUniqueStructureName(
                    structureSet.dicomStructureSet,
                    sourceRoi.name
                );

                const structureSetService = session?.structureSetService;
                if (!structureSetService) {
                    console.warn(
                        'No structure set service available for ROI copy'
                    );
                    return;
                }

                const { set } = recoilInterface;

                // Route through the tracked service so the copy emits a
                // 'created' changeset. The field population below runs
                // inside the same command — snapshots are captured at
                // flush, so the changeset carries the populated ROI.
                // A copy of the EXTERNAL structure cannot itself be
                // EXTERNAL — the domain allows one per set
                // (DuplicateExternalRoiError from createRoi), and the copy
                // must still succeed per SR-WOR-H-15-3. The duplicate is
                // created untyped; every other field still copies below.
                // The rule lives in the domain (shared with the set-level
                // copy service) and REPORTS the demotion; surfacing it is
                // this layer's job — a structure that quietly stops being the
                // body contour is exactly the kind of unexpected change the
                // user has to be told about.
                const { interpretedType: copiedType, demotedFromExternal } =
                    copyableInterpretedType(
                        structureSet.dicomStructureSet,
                        sourceRoi.interpretedType as RoiInterpretedType
                    );

                session.context.tracker.command(() => {
                    const roi = structureSetService.createRoi(
                        { id: structureSet.id },
                        copiedType,
                        newName
                    );
                    const newDicomRoi = roi.dicomRoi;

                    newDicomRoi.color = generateDarkerColor(sourceRoi.color);
                    newDicomRoi.visible = sourceRoi.visible;
                    newDicomRoi.contourThickness = sourceRoi.contourThickness;
                    newDicomRoi.contours = sourceRoi.contours.map(
                        (contour) =>
                            new DicomContour(
                                contour.type,
                                contour.data.map(
                                    (point) => point.slice() as ReadonlyVec3
                                ),
                                contour.referencedSOPClassUID,
                                contour.referencedSOPInstanceUID
                            )
                    );
                    newDicomRoi.roiDescription = sourceRoi.roiDescription;
                    newDicomRoi.roiGenerationAlgorithm =
                        sourceRoi.roiGenerationAlgorithm;
                    newDicomRoi.roiGenerationDescription =
                        sourceRoi.roiGenerationDescription;
                    newDicomRoi.electronDensityOverride =
                        sourceRoi.electronDensityOverride;
                    newDicomRoi.massDensityOverride =
                        sourceRoi.massDensityOverride;

                    // Persist the copied contours to the DICOM dataset so
                    // they are included when the structure set is exported
                    structureSet.dicomStructureSet.updateContourSequence(
                        roi.roiNumber
                    );
                    return roi;
                });

                set(
                    currentStructureSetState,
                    createStructureSetReadModel(structureSet)
                );
                set(hasUnsavedChangesState, true);
                if (demotedFromExternal) {
                    // Only after the copy has actually committed, so the
                    // message never describes a copy that did not happen.
                    enqueueSnackbar(
                        localization.t('structure.copy.externalDemotedSingle'),
                        { variant: 'warning' }
                    );
                }
                // EngineAdapter publishes RoiCreatedEvent from the tracked
                // 'created' changeset (see the creation handler); a
                // handler-side publish would double-create the engine ROI.
            } catch (error) {
                console.error(
                    'Failed to handle ROI copy:',
                    redactDicomUids(error)
                );
            }
        },
        [structureSet, session]
    );

    const handleRoiSdfCopyOverRequest = useRecoilCallback(
        () => async (event: RoiSdfCopyOverRequestedEvent) => {
            try {
                if (!structureSet) {
                    console.warn(
                        'No structure set available for SDF copy-over'
                    );
                    return;
                }

                if (event.structureSetUid !== structureSet.dicomUid) {
                    console.warn(
                        'SDF copy-over event for another structure set ignored'
                    );
                    return;
                }

                if (!env) {
                    // Unresolved edit target: writing with no resolved
                    // target would persist to the dataset while no tile
                    // shows the change (the wrong-Environment write hazard
                    // this resolver exists to close). Surface it instead of
                    // silently dropping the copy.
                    enqueueSnackbar(
                        localization.t('structure.copy.overNoActiveTarget'),
                        { variant: 'error' }
                    );
                    return;
                }

                const sourceRoi = env.getRoi(event.sourceRoiNumber);
                const targetRoi = env.getRoi(event.targetRoiNumber);

                if (!sourceRoi || !targetRoi) {
                    console.warn(
                        `Source or target ROI not found for SDF copy-over`
                    );
                    return;
                }

                // Naming the guard that actually applies: on an approved and
                // locked ROI, unlocking would not permit the copy.
                const blocker = targetRoi.contourEditBlocker;
                if (blocker) {
                    enqueueSnackbar(
                        localization.t(editBlockedMessageId(blocker)),
                        { variant: 'error' }
                    );
                    return;
                }

                const sourceTexture = sourceRoi.texture;
                if (!sourceTexture || sourceTexture.isEmpty) {
                    console.warn(
                        `Source ROI ${event.sourceRoiNumber} has no SDF data`
                    );
                    return;
                }

                const targetTexture = targetRoi.texture;
                if (!targetTexture) {
                    console.warn(
                        `Target ROI ${event.targetRoiNumber} has no texture`
                    );
                    return;
                }

                // Capture pre-copy state and publish modification-started event
                // so that the existing handler drives undo initialisation
                const prePixels = targetTexture.pixels.map((slice) =>
                    slice.slice()
                );
                const preGrid = targetTexture.textureGrid;
                await domainEventBus.publish(
                    new RoiContourModificationStartedEvent(
                        event.targetRoiNumber,
                        'copy-over-structure',
                        prePixels,
                        preGrid
                    )
                );

                // Clone source texture and assign to target. The setter bumps
                // the SDF version but does not mark the ROI modified, and the
                // `env.saveChanges()` below regenerates DICOM contours only for
                // ROIs that are — so without this the copied contours would
                // never reach the dataset while the save reported success.
                const clonedTexture = sourceTexture.clone();
                targetRoi.texture = clonedTexture;
                targetRoi.markAsChanged();

                // Capture post-copy state and publish contours-modified event
                // so that the existing handler drives undo capture + read model update
                const postPixels = clonedTexture.pixels.map((slice) =>
                    slice.slice()
                );
                const postGrid = clonedTexture.textureGrid;
                await domainEventBus.publish(
                    new RoiContoursModifiedEvent(
                        // Identity from the target ROI's owning structure
                        // set (engine ownership chain), not the selection.
                        targetRoi.structureSetUid,
                        event.targetRoiNumber,
                        'copy-over-structure',
                        postPixels,
                        postGrid,
                        prePixels,
                        preGrid
                    )
                );

                // Persist the copied contours to the DICOM dataset so
                // they are included when the structure set is exported
                env.saveChanges();
                structureSet.dicomStructureSet.updateContourSequence(
                    event.targetRoiNumber
                );

                // Trigger view update
                env.triggerContourLayerUpdate();
            } catch (error) {
                console.error(`Failed to handle SDF copy-over:`, error);
            }
        },
        [structureSet, env]
    );

    useEffect(() => {
        const subscriptions = [
            domainEventBus.subscribe(
                RoiCreationRequestedEvent.EVENT_TYPE,
                handleRoiCreationRequest
            ),
            domainEventBus.subscribe(
                RoiSelectionRequestedEvent.EVENT_TYPE,
                handleRoiSelectionRequest
            ),
            domainEventBus.subscribe(
                RoiDeletionRequestedEvent.EVENT_TYPE,
                handleRoiDeletionRequest
            ),
            domainEventBus.subscribe(
                RoiVisibilityChangeRequestedEvent.EVENT_TYPE,
                handleVisibilityChangeRequest
            ),
            domainEventBus.subscribe(
                RoiLockChangeRequestedEvent.EVENT_TYPE,
                handleLockChangeRequest
            ),
            domainEventBus.subscribe(
                RoiNameChangeRequestedEvent.EVENT_TYPE,
                handleNameChangeRequest
            ),
            domainEventBus.subscribe(
                RoiColorChangeRequestedEvent.EVENT_TYPE,
                handleColorChangeRequest
            ),
            domainEventBus.subscribe(
                RoiContourThicknessChangeRequestedEvent.EVENT_TYPE,
                handleContourThicknessChangeRequest
            ),
            domainEventBus.subscribe(
                RoiApprovalStatusChangeRequestedEvent.EVENT_TYPE,
                handleApprovalStatusChangeRequest
            ),
            domainEventBus.subscribe(
                RoiInterpretedTypeChangeRequestedEvent.EVENT_TYPE,
                handleInterpretedTypeChangeRequest
            ),
            domainEventBus.subscribe(
                RoiElectronDensityOverrideChangeRequestedEvent.EVENT_TYPE,
                handleElectronDensityOverrideChangeRequest
            ),
            domainEventBus.subscribe(
                RoiMassDensityOverrideChangeRequestedEvent.EVENT_TYPE,
                handleMassDensityOverrideChangeRequest
            ),
            domainEventBus.subscribe(
                RoiDescriptionChangeRequestedEvent.EVENT_TYPE,
                handleDescriptionChangeRequest
            ),
            domainEventBus.subscribe(
                RoiGenerationAlgorithmChangeRequestedEvent.EVENT_TYPE,
                handleGenerationAlgorithmChangeRequest
            ),
            domainEventBus.subscribe(
                RoiContoursModifiedEvent.EVENT_TYPE,
                handleContoursModified
            ),
            domainEventBus.subscribe(
                RoiPointCoordinatesChangeRequestedEvent.EVENT_TYPE,
                handlePointCoordinatesChangeRequest
            ),
            domainEventBus.subscribe(
                RoiCopyRequestedEvent.EVENT_TYPE,
                handleRoiCopyRequest
            ),
            domainEventBus.subscribe(
                RoiSdfCopyOverRequestedEvent.EVENT_TYPE,
                handleRoiSdfCopyOverRequest
            ),
        ];

        return () => {
            subscriptions.forEach((sub) => sub.unsubscribe());
        };
    }, [
        handleRoiCreationRequest,
        handleRoiDeletionRequest,
        handleVisibilityChangeRequest,
        handleLockChangeRequest,
        handleNameChangeRequest,
        handleRoiSelectionRequest,
        handleColorChangeRequest,
        handleContourThicknessChangeRequest,
        handleApprovalStatusChangeRequest,
        handleInterpretedTypeChangeRequest,
        handleElectronDensityOverrideChangeRequest,
        handleMassDensityOverrideChangeRequest,
        handleDescriptionChangeRequest,
        handleGenerationAlgorithmChangeRequest,
        handleContoursModified,
        handlePointCoordinatesChangeRequest,
        handleRoiCopyRequest,
        handleRoiSdfCopyOverRequest,
    ]);
}
